/**
 * Frame geometry: which screen shapes the dex-grid lattice is calibrated for.
 *
 * Every row and column the dex scanner reads is a fraction of the captured
 * frame (dexOcrRoi: GRID_BOTTOM 0.79, ROW_HEIGHT 0.14625 of height, four
 * columns over 0.96 of width). Those fractions were measured on 19.5:9 and
 * 20:9 phones (886x1918 and 1179x2556 iPhone frames, 923x2000 fixtures,
 * ~576x1280 Galaxy frames). Pokémon GO lays its Pokédex out by the screen's
 * width, so on a screen of another shape the cards cover a different share of
 * the height and a fixed height-fraction lattice slices them in the wrong
 * place: a 4:3 iPad is the extreme case (the first-rows pitch would be near
 * 0.24 of the height, not 0.146).
 *
 * This module classifies the frame shape. Phone shapes use the lattice the
 * scanner has always used; 4:3-class tablet shapes use the layout measured on
 * the owner's iPad screenshots of 2026-10-23 (lib/dexLayout); anything else
 * keeps the phone lattice and says so, in the frame warnings and the scan
 * log, instead of failing silently. Pinned by frameGeometry.test.ts.
 */

/**
 * Width/height band the lattice is calibrated for: 20:9 (0.45) through
 * 19:9 (0.474), with a little slack for even-pixel flooring on Android and
 * the 1920-long-edge cap on iOS. 16:9 (0.5625) and 4:3 (0.75) sit outside it,
 * as does anything wider than tall.
 */
export const CALIBRATED_FRAME_ASPECT = { min: 0.44, max: 0.49 } as const;

/**
 * Width/height band of the tablet layout: iPad 4:3 (0.75) and the 0.70-class
 * iPads (A16 at 0.695, Pro 11 at 0.698), with slack for the 1920 long-edge cap.
 */
export const TABLET_FRAME_ASPECT = { min: 0.66, max: 0.8 } as const;

export type FrameAspectClass =
  /** A portrait phone shape the lattice was measured on. */
  | "calibrated"
  /** A portrait tablet shape the tablet layout was measured on (4:3 class). */
  | "tablet"
  /** Portrait, but wider for its height than any calibrated phone (16:9). */
  | "squat"
  /** Portrait and narrower for its height than any calibrated phone (21:9+). */
  | "tall"
  /** Wider than tall: never a Pokémon GO Pokédex frame. */
  | "landscape";

export function classifyFrameAspect(width: number, height: number): FrameAspectClass {
  if (!(width > 0) || !(height > 0)) return "landscape";
  if (width > height) return "landscape";
  const aspect = width / height;
  if (aspect >= TABLET_FRAME_ASPECT.min && aspect <= TABLET_FRAME_ASPECT.max) return "tablet";
  if (aspect > CALIBRATED_FRAME_ASPECT.max) return "squat";
  if (aspect < CALIBRATED_FRAME_ASPECT.min) return "tall";
  return "calibrated";
}

export const UNCALIBRATED_FRAME_ASPECT_WARNING = "uncalibrated_frame_aspect" as const;

/**
 * The warning a portrait frame of an uncalibrated shape should carry, or null
 * for a calibrated phone or tablet. Landscape frames are rejected earlier by
 * the scanner (`not_pokedex_screenshot`) and get no warning here.
 */
export function frameAspectWarning(
  width: number,
  height: number,
): typeof UNCALIBRATED_FRAME_ASPECT_WARNING | null {
  const cls = classifyFrameAspect(width, height);
  return cls === "squat" || cls === "tall" ? UNCALIBRATED_FRAME_ASPECT_WARNING : null;
}
